'use client';

import { Eye, EyeOff, Lock } from 'lucide-react';
import React from 'react';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';

interface PasswordValidationOptions {
  minLength?: number;
  requireUppercase?: boolean;
  requireLowercase?: boolean;
  requireNumbers?: boolean;
  requireSpecialChars?: boolean;
}

interface PasswordInputProps extends React.ComponentProps<'input'> {
  strength?: boolean;
  validationOptions?: PasswordValidationOptions;
}

function PasswordInput({
  className,
  strength = false,
  validationOptions = {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
  },
  onChange,
  ...props
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [passwordStrength, setPasswordStrength] = React.useState(0);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const calculatePasswordStrength = (password: string) => {
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let score = 0;
    const {
      minLength = 8,
      requireUppercase = true,
      requireLowercase = true,
      requireNumbers = true,
      requireSpecialChars = true,
    } = validationOptions;

    const checks = [
      { enabled: true, check: () => password.length >= minLength },
      { enabled: requireUppercase, check: () => /[A-Z]/.test(password) },
      { enabled: requireLowercase, check: () => /[a-z]/.test(password) },
      { enabled: requireNumbers, check: () => /[0-9]/.test(password) },
      {
        enabled: requireSpecialChars,
        check: () => /[^A-Za-z0-9]/.test(password),
      },
    ].filter(({ enabled }) => enabled);

    const enabledChecksCount = checks.length;

    for (const { check } of checks) {
      if (check()) {
        score++;
      }
    }

    // Normalize score based on enabled checks
    const normalizedScore = Math.round((score / enabledChecksCount) * 5);
    setPasswordStrength(normalizedScore);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (strength) {
      calculatePasswordStrength(e.target.value);
    }
    // Call the parent onChange handler if provided
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Input
          className={cn(className)}
          type={showPassword ? 'text' : 'password'}
          onChange={handlePasswordChange}
          {...props}
        />
        <span className="-translate-y-1/2 absolute top-1/2 left-3">
          <Lock size={18} />
        </span>
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="-translate-y-1/2 absolute top-1/2 right-3"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {strength && (
        <div className="flex h-1 gap-1">
          {[1, 2, 3, 4, 5].map((index) => {
            let colorClass = 'bg-muted';

            if (index <= passwordStrength) {
              if (passwordStrength <= 2) {
                colorClass = 'bg-destructive';
              } else if (passwordStrength <= 3) {
                colorClass = 'bg-yellow-500';
              } else {
                colorClass = 'bg-green-500';
              }
            }

            return (
              <div
                key={index}
                className={cn(
                  'flex-1 rounded-full transition-colors',
                  colorClass
                )}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
